/** Stored package results, as returned by the computations and the report context (NaN/Infinity sent as null). */

export interface TimeSeries {
  /** ISO 8601 dates or datetimes. */
  times: string[];
  values: Record<string, (number | null)[] | undefined>;
  /** Absent in the solutions the screen builds itself. */
  units?: Record<string, string>;
}

export interface CalendarSeries {
  daily: TimeSeries;
  monthly: TimeSeries;
  yearly: TimeSeries;
}

export interface Distribution {
  total: number;
  distribution: Record<string, number>;
}

export interface PackageSolutionResult {
  num_devices: number;
  num_cleanings: number;
  /** ISO 8601 dates. */
  schedule: string[];
  /** The three following fields are absent in the solutions the screen builds itself. */
  cleaning_graph?: number[];
  production_losses_cost?: number;
  cleanings_cost?: number;
  net_present_value: number;
  /** Not used by the report; typed loosely so the screen's own solution type fits. */
  cashflow?: object | null;
  cleaning_gains: number;
  capex: Distribution;
  opex: Distribution;
  cleaning_time: Distribution;
  time_to_clean: number;
  data: CalendarSeries | null;
}

export interface PackageSolutionGroupResult {
  type?: string;
  optimal_devices: number;
  optimal_cleanings: number;
  optimal_time_to_clean?: number;
  optimal_index: number;
  solutions: PackageSolutionResult[];
}

export interface PackageComparisonResult {
  data: CalendarSeries;
  /** ISO 8601 dates. */
  rain_dates: string[];
  package_solution_groups: Record<string, PackageSolutionGroupResult>;
}

export interface SoilingAssessmentResult {
  scores: Record<string, number | null>;
  soiling: CalendarSeries;
}

/** A solution labelled with its group key; screens may pass an empty solution when a combination does not exist. */
export type NamedSolution = Partial<PackageSolutionResult> & { name: string };

/**
 * What the optimisation screens pass to the charts: their own solution types, close to the stored results but typed
 * apart (e.g. a yearly series without dates). The chart builders read them as stored results, through the helpers below.
 */
export type ScreenSolution = { name: string; [key: string]: unknown };
export type ScreenSolutionGroup = { solutions: readonly object[]; [key: string]: unknown };

export const asNamedSolutions = (solutions: ScreenSolution[]) => solutions as unknown as NamedSolution[];
export const asSolutionGroup = (group: ScreenSolutionGroup) => group as unknown as PackageSolutionGroupResult;
